import { Box, Button } from "@mui/material";
import {
  mapMerchantsFiltersToParams,
  mapMerchantsSortColumn,
  useMerchantsListQuery,
  type MerchantsListParams,
} from "Api/Queries/Merchants/useGetMerchantsQuery";
import { OpenRegistrationMerchantsTable } from "Components/Hunter/Merchants/OpenRegistration/OpenRegistrationMerchantsTable";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { PlusMathIcon } from "Components/Shared/Icons";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { useServerTable } from "Hooks/useServerTable";
import type { MerchantListItemDto } from "Infrastructure/Api/Api";
import { Link } from "Navigation/Link";
import { useMemo } from "react";
import { appUrl } from "Utils/UrlUtils";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES = Resources.Hunter.Merchants.OpenRegistrations;

const MerchantOpenRegistrationsPage = () => {
  const { t } = useResource();

  const { queryParams, tableProps } = useServerTable<MerchantListItemDto>({
    defaultSorting: [{ id: "dateCreated", desc: true }],
  });

  const apiParams = useMemo(
    (): MerchantsListParams => ({
      offset: queryParams.offset,
      limit: queryParams.limit,
      sortBy: mapMerchantsSortColumn(queryParams.sortBy),
      sortDirection: queryParams.sortDirection,
      ...mapMerchantsFiltersToParams(queryParams.filters),
      // Fixed page scope takes precedence over user filters.
      ownOnly: true,
      statuses: ["InDraft", "Onboarding"],
    }),
    [queryParams],
  );

  const { data, isLoading, isError } = useMerchantsListQuery(apiParams);

  if (isError) {
    return <ErrorText>{t(COMPONENT_RESOURCES.LoadingError)}</ErrorText>;
  }

  return (
    <AuthenticatedLayout
      title={t(COMPONENT_RESOURCES.Title)}
      backNavigation={{
        mode: "historyStack",
        fallbackUrl: appUrl("merchants"),
      }}
    >
      <StyledPageWrapper maxWidth="unset !important">
        <Box
          display="grid"
          alignItems="start"
          justifyContent="start"
          rowGap={5}
        >
          <OpenRegistrationMerchantsTable
            data={data?.items || []}
            isLoading={isLoading}
            rowCount={data?.totalCount ?? 0}
            tableProps={tableProps}
          />
          <Box
            display="flex"
            width="100%"
            justifyContent={["center", "center", "flex-start"]}
          >
            <Button
              component={Link}
              type="button"
              color="primary"
              variant="text"
              to={appUrl("merchant-new-registration")}
              startIcon={<PlusMathIcon width={20} height={20} />}
            >
              {t(COMPONENT_RESOURCES.RegisterNewMerchant)}
            </Button>
          </Box>
        </Box>
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};

export default MerchantOpenRegistrationsPage;
